import type { PortalEstado, Questionario, TipoDocumento } from '@contefy/shared';
import { ETAPA_INFO, ETAPAS, FASE_INFO } from '@contefy/shared';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Consentimento } from '../componentes/Consentimento';
import { Documentos } from '../componentes/Documentos';
import { LinhaDoTempo } from '../componentes/LinhaDoTempo';
import { Perguntas } from '../componentes/Perguntas';
import { QuestionarioForm } from '../componentes/Questionario';
import { Alerta, Selo, Spinner } from '../componentes/ui';
import { ErroApi, portal } from '../lib/api';

export function PaginaCliente() {
  const { token = '' } = useParams();
  const [estado, setEstado] = useState<PortalEstado | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const recarregar = useCallback(async () => {
    try {
      setEstado(await portal.estado(token));
      setErro(null);
    } catch (e) {
      setErro(
        e instanceof ErroApi && e.status === 404
          ? 'Este link não é válido. Confira a mensagem que você recebeu no WhatsApp.'
          : (e as Error).message,
      );
    }
  }, [token]);

  useEffect(() => {
    recarregar();
  }, [recarregar]);

  // Enquanto a IA confere os documentos, atualiza sozinho para mostrar perguntas ou o avanço de etapa.
  useEffect(() => {
    if (!estado) return;
    const ativa =
      ['documentos', 'ficha_ia'].includes(estado.abertura.etapa) && estado.documentos_completos;
    const intervalo = setInterval(recarregar, ativa ? 15_000 : 60_000);
    const aoFocar = () => recarregar();
    window.addEventListener('focus', aoFocar);
    return () => {
      clearInterval(intervalo);
      window.removeEventListener('focus', aoFocar);
    };
  }, [estado, recarregar]);

  if (erro) {
    return (
      <Layout>
        <Alerta tipo="erro">{erro}</Alerta>
      </Layout>
    );
  }
  if (!estado) {
    return (
      <Layout>
        <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
          <Spinner className="h-5 w-5" /> Carregando...
        </div>
      </Layout>
    );
  }

  const { abertura } = estado;
  const emIntake = ['documentos', 'ficha_ia'].includes(abertura.etapa);
  const bloqueado =
    !estado.envio_habilitado || ETAPAS.indexOf(abertura.etapa) > ETAPAS.indexOf('aprovacao');
  const primeiroNome = abertura.nome_contato.split(' ')[0];

  return (
    <Layout>
      <header className="mb-4">
        <p className="text-sm text-slate-500">
          Abertura de empresa · {abertura.estado} ·{' '}
          {abertura.tipo_empresa === 'EI' ? 'Empresário Individual' : 'Sociedade Limitada'}
        </p>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900">Olá, {primeiroNome}!</h1>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Selo cor="azul">
            Fase {FASE_INFO[ETAPA_INFO[abertura.etapa].fase].numero}:{' '}
            {FASE_INFO[ETAPA_INFO[abertura.etapa].fase].titulo}
          </Selo>
          {abertura.nome_empresa && (
            <span className="text-sm text-slate-600">{abertura.nome_empresa}</span>
          )}
        </div>
      </header>

      {estado.proximo_passo_cliente && (
        <div className="mb-4">
          <Alerta tipo={emIntake ? 'info' : 'sucesso'}>
            <strong>Seu próximo passo:</strong> {estado.proximo_passo_cliente}
          </Alerta>
        </div>
      )}

      <div className="space-y-4">
        {!estado.consentimento.aceito ? (
          <Consentimento
            aoAceitar={async () => {
              setEstado(await portal.aceitarConsentimento(token));
            }}
          />
        ) : (
          <>
            {!emIntake && <LinhaDoTempo estado={estado} />}

            <Perguntas
              pendencias={estado.pendencias}
              aoResponder={async (id, resposta) => {
                setEstado(await portal.responder(token, id, resposta));
              }}
            />

            <QuestionarioForm
              key={estado.questionario ? 'com' : 'sem'}
              existente={estado.questionario as Questionario | null}
              tipoEmpresa={abertura.tipo_empresa}
              estado={abertura.estado}
              nomeSugerido={abertura.nome_empresa}
              bloqueado={bloqueado}
              aoSalvar={async (q) => {
                setEstado(await portal.salvarQuestionario(token, q));
              }}
            />

            {estado.questionario && (
              <Documentos
                estado={estado}
                aoEnviar={async (tipo: TipoDocumento, socioId, arquivo) => {
                  const r = await portal.enviarDocumento(token, tipo, socioId, arquivo);
                  setEstado(r.estado);
                  return { status: r.status, motivo: r.motivo, orientacao: r.orientacao };
                }}
                aoRemover={async (id) => {
                  setEstado(await portal.removerDocumento(token, id));
                }}
              />
            )}

            {emIntake && <LinhaDoTempo estado={estado} />}
          </>
        )}
      </div>

      <footer className="mt-8 text-center text-xs text-slate-500">
        Contefy Contabilidade · Dúvidas? Responda a mensagem no WhatsApp.
      </footer>
    </Layout>
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  return <main className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-10">{children}</main>;
}
